import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Use default import for JSON file
import { Building, FileText, MapPin, Ruler, Scale } from 'lucide-react';
import { Button } from "../components/ui/button";
import CITIES_DATA from '../data/cities.json';
import { trackEvent } from "../utils/analytics";

// Define the City interface (if not already globally available)
interface City {
  id: string;
  name: string;
  slug: string;
  description: string;
  imageUrl: string;
}

const CityPage = () => {
  const { city: slugFromUrl } = useParams<{ city: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [city, setCity] = useState<City | null>(null);
  const [isValidCity, setIsValidCity] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Cast the imported JSON data to the City array type
    const citiesTyped = CITIES_DATA as City[];
    
    // Normalize the slug from the URL (remove potential prefixes)
    const slug = slugFromUrl?.replace(/^perito-judicial-em-/, '');
    
    if (slug) {
      const foundCity = citiesTyped.find(c => c.slug === slug);
      if (foundCity) {
        setCity(foundCity);
        setIsValidCity(true);
      } else {
        setIsValidCity(false);
        // Optional: redirect if city not found after checking
        // navigate('/404', { replace: true }); 
      }
    } else {
      setIsValidCity(false);
    }
    setIsLoading(false);
  }, [slugFromUrl, navigate]);

  const handleNavigateToSection = (sectionId: string) => {
    const sectionName = sectionId.replace('#', '');
    trackEvent('Navigation', 'SectionScroll', `Section: ${sectionName}`);
    
    // Navigate to home page first
    navigate("/");
    // After navigation, scroll to the section
    setTimeout(() => {
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (!city) {
    return null; // Return early if city not found
  }

  const { name: cityName, description, imageUrl } = city;
  
  // Create canonical URL in the format perito-judicial-em-cityname
  const canonicalUrl = `https://www.periciatecnica.eng.br/perito-judicial-em-${cityName}`;

  // Default fallback image if city-specific image doesn't load
  const fallbackImageUrl = '/assets/images/cities/default-city.jpg';
  
  // Split content into paragraphs for better readability
  const contentParagraphs = description.split('. ').reduce((acc, sentence, index, array) => {
    // Add sentence to current paragraph
    if (!acc[acc.length - 1]) {
      acc.push(sentence + '.');
    } else {
      acc[acc.length - 1] += ' ' + sentence + '.';
    }
    
    // Start a new paragraph every 2-3 sentences, but don't create a new one for the last sentence
    if ((index + 1) % 2 === 0 && index < array.length - 1) {
      acc.push('');
    }
    
    return acc;
  }, ['']);

  return (
    <>
      <Helmet>
        <title>Perito Judicial em {cityName} | Engenheiro Moacyr Moura Ferreira</title>
        <meta 
          name="description" 
          content={`Serviços de perícia judicial e engenharia em ${cityName}. Engenheiro perito judicial especializado em vistorias técnicas, laudos periciais e avaliações imobiliárias.`}
        />
        <meta name="keywords" content={`perito judicial ${cityName}, engenheiro perito ${cityName}, perícia técnica ${cityName}, avaliação imobiliária ${cityName}, vistoria técnica ${cityName}, laudo técnico ${cityName}, engenheiro civil ${cityName}`} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={`Perito Judicial em ${cityName} | Engenheiro Moacyr Moura Ferreira`} />
        <meta property="og:description" content={`Serviços de perícia judicial e engenharia em ${cityName}. Atendimento especializado para vistorias técnicas, laudos periciais e avaliações imobiliárias.`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://www.periciatecnica.eng.br${imageUrl}`} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Perito Judicial em ${cityName} | Engenheiro Moacyr Moura Ferreira`} />
        <meta name="twitter:description" content={`Serviços especializados de perícia judicial e engenharia em ${cityName}.`} />
        <meta name="twitter:image" content={`https://www.periciatecnica.eng.br${imageUrl}`} />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Moacyr Moura Ferreira - Perito Judicial em ${cityName}",
              "image": "/assets/images/team/moacyr.jpg",
              "url": "${canonicalUrl}",
              "telephone": "+5519991133163",
              "email": "moacyr.mmf@gmail.com",
              "description": "Engenheiro Civil especializado em Perícia Judicial e Assistência Técnica em ${cityName}. Atendimento em vistorias técnicas, avaliações imobiliárias, laudos periciais e mais.",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "${cityName}",
                "addressRegion": "SP",
                "addressCountry": "BR"
              },
              "serviceType": ["Perícia Judicial", "Assistência Técnica", "Vistorias Técnicas", "Avaliações Imobiliárias", "Laudos Técnicos"]
            }
          `}
        </script>
      </Helmet>
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-blue-600 to-blue-700 dark:from-blue-900 dark:to-blue-950 text-white py-20 md:py-28">
          <div className="absolute inset-0 opacity-20 bg-blend-multiply bg-center bg-cover" style={{ backgroundImage: `url(${imageUrl})`, backgroundPosition: 'center 30%' }}></div>
          <div className="container relative z-10 mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center">
              <div className="mb-4 inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm font-medium text-white backdrop-blur">
                <MapPin className="mr-1.5 h-3.5 w-3.5" />
                <span>Atendimento Especializado em {cityName}</span>
              </div>
              <h1 className="mb-6 text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-6xl">
                Perito Judicial em {cityName}
              </h1>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-blue-100 md:text-xl">
                Soluções técnicas especializadas em engenharia civil para laudos periciais, 
                vistorias e avaliações imobiliárias com qualidade e confiabilidade.
              </p>
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button
                  size="lg" 
                  className="w-full bg-white text-blue-700 hover:bg-blue-50 sm:w-auto"
                  onClick={() => handleNavigateToSection("#contact")}
                >
                  Solicitar Orçamento
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full border-white text-white hover:bg-blue-800 sm:w-auto"
                  asChild
                >
                  <a 
                    href={`https://wa.me/5519991133163?text=Olá,%20gostaria%20de%20mais%20informações%20sobre%20perícia%20judicial%20em%20${cityName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Falar via WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* City Info Section - Light Background with Image */}
        <section className="py-16 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 items-center">
                <div className="lg:w-1/2">
                  <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
                    Perícia Judicial em {cityName}
                  </h2>
                  
                  <div className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300">
                    {contentParagraphs.map((paragraph, index) => (
                      <p key={index} className="mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  
                  <div className="mt-8">
                    <div className="inline-flex items-center py-1 px-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm">
                      <MapPin className="h-4 w-4 mr-1" />
                      Atendemos toda a região de {cityName}
                    </div>
                  </div>
                </div>
                
                <div className="lg:w-1/2">
                  <div className="relative rounded-xl overflow-hidden shadow-xl">
                    <img 
                      src={imageUrl} 
                      alt={`Imagem da cidade de ${cityName}`}
                      className="w-full h-auto rounded-xl"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = fallbackImageUrl;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-white text-xl font-semibold">
                        Atendimento em {cityName}
                      </h3>
                      <p className="text-white/80 text-sm mt-1">
                        Especialista em perícias técnicas e avaliações com mais de 15 anos de experiência
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section - White Background */}
        <section className="py-16 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center text-gray-900 dark:text-white">
                Nossos Serviços em {cityName}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 dark:bg-gray-700 p-8 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="flex justify-center items-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-3">
                      <Scale className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Perícia Judicial</h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    Elaboração de laudos técnicos periciais para processos judiciais em {cityName}, 
                    com análises detalhadas e imparciais para auxiliar na resolução de disputas.
                  </p>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 p-8 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="flex justify-center items-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-3">
                      <Ruler className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Avaliações Imobiliárias</h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    Avaliação precisa de imóveis em {cityName} para transações imobiliárias, inventários, 
                    garantias bancárias ou processos judiciais, seguindo as normas ABNT.
                  </p>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 p-8 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="flex justify-center items-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-3">
                      <Building className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Vistorias Técnicas</h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    Vistorias detalhadas de edificações em {cityName} para identificação de patologias, 
                    análise de riscos estruturais e verificação de conformidade com normas técnicas.
                  </p>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 p-8 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="flex justify-center items-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-3">
                      <FileText className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Assistência Técnica</h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    Assessoria técnica especializada em {cityName} para análise crítica de laudos 
                    periciais e suporte técnico em processos judiciais e extrajudiciais.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-blue-50 dark:bg-blue-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
                Precisa de um perito judicial em {cityName}?
              </h2>
              <p className="text-lg mb-8 text-gray-700 dark:text-gray-300">
                Entre em contato para uma consultoria inicial e descubra como nossos serviços 
                podem auxiliar em seu caso específico.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => handleNavigateToSection("#contact")}
                >
                  Solicitar Orçamento
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-800/30"
                  onClick={() => handleNavigateToSection("#about")}
                >
                  Conheça Moacyr
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default CityPage; 